import React from 'react';
import { Box, Paper, Typography, alpha, useTheme } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { Reconciliation } from '../../../../types/finance';
import { useAuth } from '../../../../context/AuthContext';
import { useTimezone } from '../../../../hooks/useTimezone';
import { formatPaisa } from '../../../../utils/paisa';
import Amount from '../../ledger/Amount';

/**
 * The reconciliation as one sum: last reconciled + cleared in − cleared out
 * = cleared balance, set against the statement. The difference is what's
 * left to find.
 */
const ReconciliationSummary: React.FC<{ rec: Reconciliation }> = ({ rec }) => {
  const theme = useTheme();
  const { currency } = useAuth();
  const { formatDate } = useTimezone();
  const s = rec.summary;
  const balanced = s.differencePaisa === 0;

  const cells: Array<{ label: string; sign?: string; paisa: number; strong?: boolean }> = [
    { label: 'Last reconciled balance', paisa: s.openingPaisa },
    { label: 'Cleared in', sign: '+', paisa: s.clearedInPaisa },
    { label: 'Cleared out', sign: '−', paisa: s.clearedOutPaisa },
    { label: 'Cleared balance', sign: '=', paisa: s.clearedBalancePaisa, strong: true },
    { label: `Statement · ${formatDate(rec.statementDate)}`, paisa: s.statementBalancePaisa, strong: true },
  ];

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr) 1.2fr' } }}>
      {cells.map((c, i) => (
        <Box key={c.label} sx={{ p: 2, borderRight: { md: 1 }, borderBottom: { xs: 1, md: 0 }, borderColor: { xs: 'divider', md: 'divider' }, ...(i === 4 && { bgcolor: alpha(theme.palette.text.primary, 0.02) }) }}>
          <Typography variant="caption" color="text.secondary" noWrap display="block">{c.label}</Typography>
          <Typography variant={c.strong ? 'h6' : 'subtitle1'} fontWeight={c.strong ? 700 : 500} sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
            {c.sign && <Box component="span" sx={{ color: 'text.disabled', mr: 0.75, fontWeight: 400 }}>{c.sign}</Box>}
            {c.paisa < 0 ? `(${formatPaisa(-c.paisa)})` : formatPaisa(c.paisa)}
          </Typography>
        </Box>
      ))}
      <Box sx={{
        p: 2, gridColumn: { xs: '1 / -1', md: 'auto' },
        bgcolor: alpha(balanced ? theme.palette.success.main : theme.palette.warning.main, 0.08),
        color: balanced ? 'success.dark' : 'warning.dark',
      }}>
        <Typography variant="caption" fontWeight={600} display="block">Difference</Typography>
        {balanced ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <CheckCircle fontSize="small" />
            <Typography variant="h6" fontWeight={700}>Balanced</Typography>
          </Box>
        ) : (
          <Amount paisa={s.differencePaisa} blankZero={false} variant="h6" fontWeight={700} color="inherit" />
        )}
        {!balanced && (
          <Typography variant="caption" display="block" sx={{ opacity: 0.85 }}>
            The statement is {s.differencePaisa > 0 ? 'higher' : 'lower'} by {currency} {formatPaisa(Math.abs(s.differencePaisa))}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

export default ReconciliationSummary;
